import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Car, Package, User, CheckCircle } from 'lucide-react';
import { handleBooking } from '../utils/mock';

const BookingForm = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    vehicleType: '',
    serviceType: '',
    addOns: [],
    name: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    address: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const vehicleTypes = [
    { value: 'car', label: 'Sedan/Car', icon: '🚗', price: '$65-85' },
    { value: 'suv', label: 'SUV', icon: '🚙', price: '$75-95' },
    { value: 'truck', label: 'Truck/Large SUV', icon: '🚚', price: '$85-115' }
  ];

  const serviceTypes = [
    { value: 'interior', label: 'Interior Only', description: 'Complete interior detailing' },
    { value: 'exterior', label: 'Exterior Only', description: 'Premium exterior care' },
    { value: 'full', label: 'Full Detail', description: 'Interior + Exterior package' }
  ];

  const addOnOptions = [
    { value: 'claybar', label: 'Clay Bar Treatment', price: '$40' },
    { value: 'shampoo', label: 'Car Shampoo', price: '$40' },
    { value: 'pethair', label: 'Pet Hair Removal', price: '$30' },
    { value: 'leather', label: 'Leather Conditioner', price: '$25' }
  ];

  const handleVehicleSelect = (value) => {
    setFormData({ ...formData, vehicleType: value });
    setTimeout(() => setStep(2), 300);
  };

  const handleServiceSelect = (value) => {
    setFormData({ ...formData, serviceType: value });
    setTimeout(() => setStep(3), 300);
  };

  const toggleAddOn = (value) => {
    const current = formData.addOns;
    if (current.includes(value)) {
      setFormData({ ...formData, addOns: current.filter(item => item !== value) });
    } else {
      setFormData({ ...formData, addOns: [...current, value] });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const result = handleBooking(formData);
    if (result.success) {
      setSubmitted(true);
    }
  };

  const resetForm = () => {
    setStep(1);
    setFormData({
      vehicleType: '',
      serviceType: '',
      addOns: [],
      name: '',
      email: '',
      phone: '',
      preferredDate: '',
      preferredTime: '',
      address: ''
    });
    setSubmitted(false);
  };

  if (submitted) {
    return (
      <section id="booking-section" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-black via-zinc-950 to-black">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-zinc-900/50 backdrop-blur-sm border-2 border-yellow-500">
            <CardContent className="pt-12 pb-12 text-center">
              <div className="w-20 h-20 rounded-full bg-gradient-to-r from-yellow-500 to-amber-400 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-black" />
              </div>
              <h3 className="text-3xl font-bold text-white mb-4">Booking Received!</h3>
              <p className="text-gray-400 mb-6 text-lg">
                Thank you for choosing Gio's Car Detailing. We'll text you shortly to confirm your appointment.
              </p>
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
                <p className="text-yellow-400 font-medium">📱 We'll contact you at: {formData.phone}</p>
              </div>
              <Button 
                onClick={resetForm}
                className="bg-gradient-to-r from-yellow-500 to-amber-400 hover:from-yellow-600 hover:to-amber-500 text-black font-bold"
              >
                Book Another Detail
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>
    );
  }

  return (
    <section id="booking-section" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-black via-zinc-950 to-black">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Book Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-amber-400">Detail</span>
          </h2>
          <p className="text-gray-400 text-lg">Simple 3-step booking process</p>
        </div>

        {/* Progress Indicator */}
        <div className="flex justify-center items-center gap-4 mb-12">
          {[1, 2, 3].map((num) => (
            <React.Fragment key={num}>
              <div className={`flex items-center justify-center w-12 h-12 rounded-full font-bold transition-all duration-300 ${
                step >= num 
                  ? 'bg-gradient-to-r from-yellow-500 to-amber-400 text-black scale-110' 
                  : 'bg-zinc-800 text-gray-500'
              }`}>
                {num}
              </div>
              {num < 3 && (
                <div className={`h-1 w-16 transition-all duration-300 ${
                  step > num ? 'bg-yellow-500' : 'bg-zinc-800'
                }`}></div>
              )}
            </React.Fragment>
          ))}
        </div>

        <Card className="bg-zinc-900/50 backdrop-blur-sm border-2 border-yellow-500/20">
          <CardHeader>
            <CardTitle className="text-2xl text-white flex items-center gap-3">
              {step === 1 && <><Car className="text-yellow-400" /> Select Your Vehicle</>}
              {step === 2 && <><Package className="text-yellow-400" /> Choose Your Service</>}
              {step === 3 && <><User className="text-yellow-400" /> Your Information</>}
            </CardTitle>
            <CardDescription className="text-gray-400">
              {step === 1 && 'What type of vehicle do you have?'}
              {step === 2 && 'Select the detailing package you need'}
              {step === 3 && 'Tell us when and where to come'}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {/* Step 1: Vehicle Selection */}
            {step === 1 && (
              <div className="grid md:grid-cols-3 gap-4">
                {vehicleTypes.map((vehicle) => (
                  <button
                    key={vehicle.value}
                    onClick={() => handleVehicleSelect(vehicle.value)}
                    className={`p-6 rounded-xl border-2 transition-all duration-300 hover:scale-105 ${
                      formData.vehicleType === vehicle.value
                        ? 'border-yellow-500 bg-yellow-500/10'
                        : 'border-yellow-500/20 bg-zinc-800/50 hover:border-yellow-500/50'
                    }`}
                  >
                    <div className="text-5xl mb-3">{vehicle.icon}</div>
                    <h3 className="text-white font-bold mb-2">{vehicle.label}</h3>
                    <p className="text-yellow-400 font-semibold">{vehicle.price}</p>
                  </button>
                ))}
              </div>
            )}

            {/* Step 2: Service Selection */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="space-y-4">
                  {serviceTypes.map((service) => (
                    <button
                      key={service.value}
                      onClick={() => handleServiceSelect(service.value)}
                      className={`w-full p-6 rounded-xl border-2 transition-all duration-300 text-left hover:scale-102 ${
                        formData.serviceType === service.value
                          ? 'border-yellow-500 bg-yellow-500/10'
                          : 'border-yellow-500/20 bg-zinc-800/50 hover:border-yellow-500/50'
                      }`}
                    >
                      <h3 className="text-white font-bold text-lg mb-2">{service.label}</h3>
                      <p className="text-gray-400 text-sm">{service.description}</p>
                    </button>
                  ))}
                </div>

                <div className="pt-6 border-t border-yellow-500/20">
                  <h4 className="text-white font-semibold mb-4">Add Premium Services (Optional)</h4>
                  <div className="grid md:grid-cols-2 gap-3">
                    {addOnOptions.map((addon) => (
                      <button
                        key={addon.value}
                        onClick={() => toggleAddOn(addon.value)}
                        className={`p-4 rounded-lg border transition-all duration-300 text-left ${
                          formData.addOns.includes(addon.value)
                            ? 'border-yellow-500 bg-yellow-500/10'
                            : 'border-yellow-500/20 bg-zinc-800/30 hover:border-yellow-500/50'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <span className="text-white font-medium text-sm">{addon.label}</span>
                          <span className="text-yellow-400 font-bold text-sm">{addon.price}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <Button 
                  onClick={() => setStep(1)}
                  variant="outline"
                  className="w-full border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                >
                  Back to Vehicle Selection
                </Button>
              </div>
            )}

            {/* Step 3: Contact Information */}
            {step === 3 && (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name" className="text-white mb-2 block">Full Name *</Label>
                    <Input
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-zinc-800 border-yellow-500/30 text-white focus:border-yellow-500"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-white mb-2 block">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-zinc-800 border-yellow-500/30 text-white focus:border-yellow-500"
                      placeholder="(443) 942-1539"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-white mb-2 block">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="bg-zinc-800 border-yellow-500/30 text-white focus:border-yellow-500"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <Label htmlFor="address" className="text-white mb-2 block">Service Address *</Label>
                  <Input
                    id="address"
                    required
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="bg-zinc-800 border-yellow-500/30 text-white focus:border-yellow-500"
                    placeholder="123 Main St, City, State"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="date" className="text-white mb-2 block">Preferred Date *</Label>
                    <Input
                      id="date"
                      type="date"
                      required
                      value={formData.preferredDate}
                      onChange={(e) => setFormData({ ...formData, preferredDate: e.target.value })}
                      className="bg-zinc-800 border-yellow-500/30 text-white focus:border-yellow-500"
                    />
                  </div>
                  <div>
                    <Label htmlFor="time" className="text-white mb-2 block">Preferred Time *</Label>
                    <Input
                      id="time"
                      type="time"
                      required
                      value={formData.preferredTime}
                      onChange={(e) => setFormData({ ...formData, preferredTime: e.target.value })}
                      className="bg-zinc-800 border-yellow-500/30 text-white focus:border-yellow-500"
                    />
                  </div>
                </div>

                <div className="flex gap-4">
                  <Button 
                    type="button"
                    onClick={() => setStep(2)}
                    variant="outline"
                    className="flex-1 border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                  >
                    Back
                  </Button>
                  <Button 
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-yellow-500 to-amber-400 hover:from-yellow-600 hover:to-amber-500 text-black font-bold"
                  >
                    Confirm Booking
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default BookingForm;
